import clsx from "clsx";
import { EditIcon } from "lucide-react";
import { Button } from "@/components/Button/Button";
import { SmartMatchIcon } from "@/components/Icons/Icons";
import { JobActionsMenu } from "@/components/Jobs/JobActionsMenu";
import { JobStatusSelector } from "@/components/Jobs/JobStatusSelector";
import { UserJobsEmptyState } from "@/components/Jobs/JobsEmptyState";
import { AppText } from "@/components/Text/AppText";
import { IUserJob } from "@/types/userJob";

export const UserJobsList = ({
    userJobs,
    showArchived,
    onCreateJob,
    activeJob,
    onJobSelect,
    onEditJob,
    onQuickUpdateJob,
    openSmartMatchModal,
    canRenderColumn,
    handleDeleteJob,
    openJobResumesModal,
}: {
    userJobs: IUserJob[];
    showArchived: boolean;
    onCreateJob: () => void;
    activeJob?: IUserJob;
    onJobSelect?: (job: IUserJob) => void;
    onEditJob: (job: IUserJob) => void;
    onQuickUpdateJob: (job: IUserJob, data: Partial<IUserJob>) => void;
    openSmartMatchModal: (job: IUserJob) => void;
    canRenderColumn: (column: string) => boolean;
    handleDeleteJob: (job: IUserJob) => void;
    openJobResumesModal: (job: IUserJob) => void;
}) => {
    return (
        <div className="lg:hidden">
            {userJobs.length === 0 ? (
                <UserJobsEmptyState
                    showArchived={showArchived}
                    onCreateJob={onCreateJob}
                />
            ) : (
                <div className="space-y-4">
                    {userJobs.map(job => (
                        <div
                            key={job.id}
                            className={clsx(
                                "relative rounded-lg bg-white p-4 shadow-sm ring-2 ring-inset",
                                activeJob?.id === job.id
                                    ? "ring-primary-700"
                                    : "ring-neutral-300",
                            )}
                            onClick={
                                canRenderColumn("select")
                                    ? () => onJobSelect?.(job)
                                    : undefined
                            }
                        >
                            {/* Header Section */}
                            <div className="mb-2 flex items-start justify-between gap-3 border-b border-neutral-100 pb-3">
                                <div>
                                    <AppText
                                        variant="contextheading"
                                        className="line-clamp-1 text-neutral-900"
                                    >
                                        {job.jobTitle}
                                    </AppText>
                                    {!!job.companyName && (
                                        <AppText
                                            variant="regular"
                                            className="mt-1 line-clamp-1 text-neutral-700"
                                        >
                                            {job.companyName}
                                        </AppText>
                                    )}
                                </div>
                                {canRenderColumn("actions") &&
                                    !showArchived && (
                                        <button
                                            onClick={() => onEditJob(job)}
                                            className="rounded-lg p-1 hover:bg-neutral-100"
                                        >
                                            <EditIcon className="h-5 w-5" />
                                        </button>
                                    )}
                                {canRenderColumn("select") && (
                                    <input
                                        type="radio"
                                        checked={activeJob?.id === job.id}
                                        onChange={() => onJobSelect?.(job)}
                                        className="h-4 w-4 cursor-pointer rounded-full border-2 border-primary-700 text-primary-700 focus:ring-transparent"
                                    />
                                )}
                            </div>

                            {/* Info Grid */}
                            <div className="mb-2 flex items-center gap-4">
                                <div>
                                    <JobStatusSelector
                                        status={job.status}
                                        onChange={status => {
                                            onQuickUpdateJob(job, { status });
                                        }}
                                        readOnly={showArchived || !!onJobSelect}
                                    />
                                </div>
                                <div className="hidden items-center gap-2 border-l border-neutral-200 pl-4 xs:flex">
                                    <AppText
                                        variant="labelsbuttons"
                                        className="line-clamp-1 text-neutral-900"
                                    >
                                        {job.location || "—"}
                                    </AppText>
                                </div>
                                <div className="hidden items-center gap-2 border-l border-neutral-200 pl-4 sm:flex">
                                    <AppText
                                        variant="labelsbuttons"
                                        className="line-clamp-1 text-neutral-900"
                                    >
                                        {job.createdAt
                                            ? new Date(
                                                  job.createdAt,
                                              ).toLocaleDateString()
                                            : "—"}
                                    </AppText>
                                </div>
                            </div>

                            {/* Footer Actions Section */}
                            {canRenderColumn("actions") && (
                                <div
                                    className={clsx(
                                        "flex items-center border-t border-neutral-100 pt-3",
                                        showArchived
                                            ? "justify-end"
                                            : "justify-between",
                                    )}
                                >
                                    {!showArchived && (
                                        <div className="flex items-center gap-2">
                                            <Button
                                                color="gradient"
                                                variant="outline"
                                                onClick={() =>
                                                    openSmartMatchModal(job)
                                                }
                                                leftIcon={
                                                    <SmartMatchIcon
                                                        gradient
                                                        className="h-7 w-7"
                                                    />
                                                }
                                            >
                                                Tailor Resume
                                            </Button>
                                            {/* {job.linkedResumeIds?.length ? (
                                                <Button
                                                    color="softIndigo"
                                                    className="!h-8 whitespace-nowrap"
                                                    onClick={() =>
                                                        openJobResumesModal(job)
                                                    }
                                                >
                                                    View Resumes (
                                                    {
                                                        job.linkedResumeIds
                                                            ?.length
                                                    }
                                                    )
                                                </Button>
                                            ) : (
                                                <Button
                                                    color="primary"
                                                    className="!h-8 whitespace-nowrap"
                                                    onClick={() =>
                                                        openJobResumesModal(job)
                                                    }
                                                >
                                                    Attach Resumes
                                                </Button>
                                            )} */}
                                        </div>
                                    )}
                                    <JobActionsMenu
                                        job={job}
                                        showArchived={showArchived}
                                        onEditJob={onEditJob}
                                        onArchiveJob={job => {
                                            onQuickUpdateJob(job, {
                                                archivedAt: !job.archivedAt
                                                    ? new Date().toISOString()
                                                    : null,
                                            });
                                        }}
                                        onDeleteJob={handleDeleteJob}
                                        variant="mobile"
                                    />
                                </div>
                            )}
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
};
